import { useCallback, useEffect, useRef } from 'react';

import { useRestoreEstimateData } from './useRestoreEstimateData';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { guidToShortGuid, notifyFromStatus } from '~/core/helpers';
import { setLocalData } from '~/core/helpers/storageHelper';
import { LoadEstimateKeys } from '~/core/hooks/useEstimate';
import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import {
  EstimateStatus,
  GetEstimateDto,
} from '~/core/models/save-estimate-dto';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectCurrentEstimateCode,
  selectIsSavingEstimate,
  updateCurrentEstimate,
  updateEeStatusList,
  updateLoadEstimateKeys,
  updateShowListOfEstimates,
} from '~/redux/slices/estimate';
import { updateZipcode } from '~/redux/slices/zipcode';
import { getNotificationsFor } from '~legacy/components';

export const useEstimateLoader = (loadEstimateCode: string | undefined) => {
  const {
    fetchedEstimate,
    isFetchingEstimate,
    isError: isFetchError,
  } = useFetchedEstimate();

  const dispatch = useAppDispatch();
  const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
  const currentEstimateCode = useAppSelector(selectCurrentEstimateCode);
  const { restoreFromFetchedEstimate } = useRestoreEstimateData();
  const isEstimateFetched = useRef<boolean>(false);
  const estimateLoaded = useRef<boolean>(false);

  const loadEstimateAndEnableSteps = useCallback(
    (loadedEstimateDto: GetEstimateDto) => {
      restoreFromFetchedEstimate(loadedEstimateDto);
      dispatch(updateShowListOfEstimates(true));
      setLocalData(LOCAL_STORAGE_KEY.KEEP_LOAD_ESTIMATE_IN_URL, true);
    },
    [dispatch, restoreFromFetchedEstimate]
  );

  const resetLoadedFlags = useCallback(() => {
    isEstimateFetched.current = false;
    estimateLoaded.current = false;
  }, []);

  useEffect(() => {
    if (loadEstimateCode) {
      dispatch(
        updateLoadEstimateKeys({
          code: guidToShortGuid(loadEstimateCode),
        } as LoadEstimateKeys)
      );
    }
  }, [dispatch, loadEstimateCode]);

  // Update page title when estimate changes
  useEffect(() => {
    if (fetchedEstimate) {
      document.title = `${fetchedEstimate.customerName}: ${fetchedEstimate.estimateNumber}`;
    }
  }, [fetchedEstimate]);

  useEffect(() => {
    const isInProcessOrError =
      isFetchingEstimate || isFetchError || isSavingEstimate;

    if (fetchedEstimate && (isEstimateFetched.current || !isInProcessOrError)) {
      if (fetchedEstimate.estimateData) {
        dispatch(updateCurrentEstimate(fetchedEstimate.estimateData));
      }

      isEstimateFetched.current = true;
      dispatch(updateZipcode(fetchedEstimate.customerZip));
    }
  }, [
    dispatch,
    fetchedEstimate,
    isFetchError,
    isFetchingEstimate,
    isSavingEstimate,
  ]);

  useEffect(() => {
    if (isEstimateFetched.current && !fetchedEstimate) {
      isEstimateFetched.current = false;
      estimateLoaded.current = false;
      dispatch(updateShowListOfEstimates(false));
    }
  }, [dispatch, fetchedEstimate]);

  useEffect(() => {
    if (
      fetchedEstimate &&
      fetchedEstimate.estimateData &&
      isEstimateFetched.current &&
      loadEstimateCode !== currentEstimateCode &&
      !estimateLoaded.current
    ) {
      loadEstimateAndEnableSteps(fetchedEstimate);
      getNotificationsFor(fetchedEstimate?.estimateNumber); // EE3-756 - load any _saved_ notifications for the estimate

      const makeCurrentStatus = (estimate: GetEstimateDto): EstimateStatus => ({
        imeMessage: null,
        imeStatus: null,
        status: estimate.status,
        paymentUrl: estimate.paymentUrl || null,
      });

      // notify based on the fetched estimate status
      const statusList = notifyFromStatus({
        currentStatus: makeCurrentStatus(fetchedEstimate), // generate a status record based on the fetched estimate
        estimate: fetchedEstimate,
      });

      // and update the list of statuses in state (not sure if these are used)
      dispatch(updateEeStatusList(statusList));

      estimateLoaded.current = true;
      setLocalData(LOCAL_STORAGE_KEY.FIRST_SHAPE_LOAD, false);
    }
  }, [
    currentEstimateCode,
    dispatch,
    fetchedEstimate,
    isFetchError,
    isFetchingEstimate,
    loadEstimateAndEnableSteps,
    loadEstimateCode,
  ]);

  return {
    fetchedEstimate,
    isFetchError,
    isFetchingEstimate,
    resetLoadedFlags,
  };
};
